import React, { useState, useEffect } from "react";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
} from "chart.js";
import { withTranslation } from "react-i18next";
import { withStyles } from "@material-ui/core/styles";
import { Box, Typography, Grid, TextField } from "@material-ui/core";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { CircularProgress } from "@mui/material";

import styles from "./StatisticsStyles";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  Filler
);

const StatisticsView = ({ classes, t, analytics, isLoading }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState("thisYear");

  // Fonction pour obtenir les mois à afficher
  const getMonthsToDisplay = () => {
    const currentDate = new Date();
    const months = [];

    if (selectedPeriod === "thisYear") {
      // Pour les 12 derniers mois (incluant le mois courant)
      for (let i = 11; i >= 0; i--) {
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        months.push(`${month}/${year}`);
      }
    } else {
      // Pour les 12 mois précédents (année précédente)
      for (let i = 23; i >= 12; i--) {
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        months.push(`${month}/${year}`);
      }
    }

    return months;
  };

  // Fonction pour regrouper les analytics par mois
  const getAnalyticsByMonth = () => {
    const analyticsByMonth = new Array(12).fill(0);
    const currentDate = new Date();
    const months = getMonthsToDisplay();

    if (analytics && analytics.length > 0) {
      analytics.forEach((analytic) => {
        const date = new Date(analytic.createdAt);

        // Trouver l'index du mois correspondant
        const analyticMonth = String(date.getMonth() + 1).padStart(2, "0");
        const analyticYear = date.getFullYear();
        const monthKey = `${analyticMonth}/${analyticYear}`;

        const monthIndex = months.indexOf(monthKey);

        if (monthIndex !== -1) {
          analyticsByMonth[monthIndex]++;
        }
      });
    }

    return analyticsByMonth;
  };

  // Fonction pour calculer les analytics par parcours
  const getAnalyticsByTour = () => {
    const analyticsByTour = {};
    const currentDate = new Date();
    const months = getMonthsToDisplay();

    if (analytics && analytics.length > 0) {
      analytics.forEach((analytic) => {
        const date = new Date(analytic.createdAt);

        // Vérifier si l'analytic est dans la période sélectionnée
        const analyticMonth = String(date.getMonth() + 1).padStart(2, "0");
        const analyticYear = date.getFullYear();
        const monthKey = `${analyticMonth}/${analyticYear}`;

        if (months.includes(monthKey)) {
          const tourName = analytic.tour?.title || "Sans nom";
          analyticsByTour[tourName] = (analyticsByTour[tourName] || 0) + 1;
        }
      });
    }

    return {
      labels: Object.keys(analyticsByTour),
      data: Object.values(analyticsByTour),
    };
  };

  // Fonction pour obtenir les analytics par mois et par parcours
  const getAnalyticsByMonthAndTour = () => {
    const currentDate = new Date();
    const analyticsByTour = {};
    const months = getMonthsToDisplay();

    // Initialiser la structure de données
    months.forEach((month) => {
      analyticsByTour[month] = {};
    });

    if (analytics && analytics.length > 0) {
      analytics.forEach((analytic) => {
        const date = new Date(analytic.createdAt);
        const tourName = analytic.tour?.title || "Sans nom";

        // Vérifier si l'analytic est dans la période sélectionnée
        const analyticMonth = String(date.getMonth() + 1).padStart(2, "0");
        const analyticYear = date.getFullYear();
        const monthKey = `${analyticMonth}/${analyticYear}`;

        if (months.includes(monthKey)) {
          analyticsByTour[monthKey][tourName] =
            (analyticsByTour[monthKey][tourName] || 0) + 1;
        }
      });
    }

    console.log("getAnalyticsByMonthAndTour - months:", months);
    console.log("getAnalyticsByMonthAndTour - result:", analyticsByTour);
    return analyticsByTour;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#000",
        bodyColor: "#666",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        displayColors: true,
        callbacks: {
          title: function (tooltipItems) {
            return getMonthsToDisplay()[tooltipItems[0].dataIndex];
          },
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += context.parsed.y;
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          font: {
            size: 12,
          },
        },
      },
      y: {
        stacked: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          font: {
            size: 12,
          },
          padding: 10,
        },
        beginAtZero: true,
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  const pieOptions = {
    ...options,
    plugins: {
      ...options.plugins,
      legend: {
        display: true,
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.parsed}`;
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const analyticsData = getAnalyticsByTour();
  const monthlyData = getAnalyticsByMonthAndTour();
  const months = getMonthsToDisplay();

  const barData = {
    labels: months,
    datasets: (analyticsData?.labels || []).map((tourName, index) => ({
      label: tourName,
      data: months.map((month) => monthlyData[month][tourName] || 0),
      backgroundColor: [
        "rgba(94, 119, 255, 0.8)",
        "rgba(75, 192, 192, 0.8)",
        "rgba(255, 159, 64, 0.8)",
        "rgba(255, 99, 132, 0.8)",
        "rgba(153, 102, 255, 0.8)",
        "rgba(54, 162, 235, 0.8)",
        "rgba(255, 206, 86, 0.8)",
        "rgba(75, 192, 192, 0.8)",
        "rgba(255, 159, 64, 0.8)",
        "rgba(255, 99, 132, 0.8)",
      ][index % 10],
      borderColor: [
        "rgba(94, 119, 255, 1)",
        "rgba(75, 192, 192, 1)",
        "rgba(255, 159, 64, 1)",
        "rgba(255, 99, 132, 1)",
        "rgba(153, 102, 255, 1)",
        "rgba(54, 162, 235, 1)",
        "rgba(255, 206, 86, 1)",
        "rgba(75, 192, 192, 1)",
        "rgba(255, 159, 64, 1)",
        "rgba(255, 99, 132, 1)",
      ][index % 10],
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 12,
    })),
  };

  // Données pour le graphique en ligne
  const lineData = {
    labels: getMonthsToDisplay(),
    datasets: [
      {
        label: t("statistics.sessions"),
        data: getAnalyticsByMonth(),
        fill: true,
        backgroundColor: "rgba(75, 192, 192, 0.1)",
        borderColor: "rgba(75, 192, 192, 1)",
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: "rgba(75, 192, 192, 1)",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(75, 192, 192, 1)",
        pointHoverBorderWidth: 2,
      },
    ],
  };

  // Données pour le graphique en anneau (répartition des analytics par parcours)
  const doughnutData = {
    labels: getAnalyticsByTour().labels,
    datasets: [
      {
        data: getAnalyticsByTour().data,
        backgroundColor: [
          "rgba(94, 119, 255, 0.8)",
          "rgba(75, 192, 192, 0.8)",
          "rgba(255, 159, 64, 0.8)",
          "rgba(255, 99, 132, 0.8)",
          "rgba(153, 102, 255, 0.8)",
          "rgba(54, 162, 235, 0.8)",
          "rgba(255, 206, 86, 0.8)",
          "rgba(75, 192, 192, 0.8)",
          "rgba(255, 159, 64, 0.8)",
          "rgba(255, 99, 132, 0.8)",
        ],
        borderColor: [
          "rgba(94, 119, 255, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 2,
        cutout: "70%",
        spacing: 2,
      },
    ],
  };

  // Données pour le graphique des sessions
  const visitorsData = {
    labels: getMonthsToDisplay(),
    datasets: [
      {
        label: t("statistics.analyticsCount"),
        data: getAnalyticsByMonth(),
        fill: true,
        backgroundColor: "rgba(67, 83, 255, 0.1)",
        borderColor: "rgba(67, 83, 255, 1)",
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: "rgba(67, 83, 255, 1)",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(67, 83, 255, 1)",
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const visitorOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#000",
        bodyColor: "#666",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        displayColors: false,
        callbacks: {
          title: function (tooltipItems) {
            return getMonthsToDisplay()[tooltipItems[0].dataIndex];
          },
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += context.parsed.y;
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          font: {
            size: 11,
          },
          padding: 5,
          callback: function (value) {
            return getMonthsToDisplay()[value];
          },
        },
      },
      y: {
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          font: {
            size: 11,
          },
          padding: 5,
        },
        beginAtZero: true,
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.headerContainer}>
          <Typography variant="h4" className={classes.dashboardTitle}>
            {t("statistics.analyticsTitle")}
          </Typography>
          <Typography variant="body1" className={classes.dashboardSubtitle}>
            {t("statistics.analyticsDescription")}
          </Typography>
        </Box>

        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              className={classes.periodSelector}
              style={{ marginBottom: "20px" }}
            >
              <button
                className={`${classes.periodButton} ${
                  selectedPeriod === "lastYear"
                    ? classes.periodButtonActive
                    : ""
                }`}
                onClick={() => setSelectedPeriod("lastYear")}
              >
                {t("statistics.lastYear")}
              </button>
              <button
                className={`${classes.periodButton} ${
                  selectedPeriod === "thisYear"
                    ? classes.periodButtonActive
                    : ""
                }`}
                onClick={() => setSelectedPeriod("thisYear")}
              >
                {t("statistics.thisYear")}
              </button>
            </Box>

            <Box className={classes.visitorOverview}>
              <Typography variant="h6" className={classes.title}>
                {t("statistics.sessionsCount")}
              </Typography>
              <Box className={classes.visitorChart}>
                <Line
                  options={visitorOptions}
                  data={visitorsData}
                  height={300}
                />
              </Box>
            </Box>

            <Box className={classes.dateSelectorContainer}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      className={classes.dateSelector}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  label={t("statistics.selectDate")}
                  format="dd/MM/yyyy"
                  views={["year", "month", "date"]}
                />
              </LocalizationProvider>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box className={classes.statsBlock}>
                  <Typography variant="h6" className={classes.title}>
                    {t("statistics.sessionsPerMonthAndTour")}
                  </Typography>
                  <Box
                    className={classes.chartContainer}
                    style={{ minHeight: "500px" }}
                  >
                    <Bar options={options} data={barData} />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box className={classes.statsBlock}>
                  <Typography variant="h6" className={classes.title}>
                    {t("statistics.sessionsDistributionByTour")}
                  </Typography>
                  <Box
                    className={classes.chartContainer}
                    style={{
                      minHeight: "500px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ width: "100%", height: "100%" }}>
                      <Doughnut options={pieOptions} data={doughnutData} />
                    </div>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

const WrappedComponent = withStyles(styles)(StatisticsView);
export default withTranslation()(WrappedComponent);
